/**
 * Foundry Agent Manifest
 *
 * Operation manifest for "Foundry agent" — an agent backed by
 * Azure AI Foundry.  The user selects (or creates) an agent in a Foundry
 * project; model, instructions and built-in tools are populated from the
 * remote agent definition.
 *
 * Shares the same connection type (`agent`) and connector as the native
 * agent manifest (agentloop.ts), but uses `agentModelType: 'FoundryAgentService'`.
 */

import type { OperationManifest, ConnectorInfo } from '@/types/operationManifest';
import { OperationOptions, SettingScope } from '@/types/operationManifest';

// Foundry-agent badge: Fluent `BotSparkleFilled` on the purple
// `agentFoundryBrand` tile (`ottoTokens.agentFoundryBrand` = `#8B5CF6`).
// The picker icon, the canvas badge, and `AgentVariantIcon('foundry')`
// all read as the same variant. Path is the exact `BotSparkleFilled`
// outline from `@fluentui/react-icons`.
const FOUNDRY_AGENT_BRAND = '#8B5CF6';
const FOUNDRY_AGENT_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none"><rect width="22" height="22" rx="4" fill="#8B5CF6"/><g transform="translate(1,1)" fill="#FFFFFF"><path d="M14.48 8c.18 0 .35.03.52.1V4.5c0-.83-.67-1.5-1.5-1.5h-3v-.5c0-.3-.22-.5-.5-.5a.5.5 0 0 0-.5.5V3h-3C5.67 3 5 3.67 5 4.5v4c0 .83.67 1.5 1.5 1.5h6.3l.33-1.04v-.01A1.42 1.42 0 0 1 14.49 8ZM7 6.5a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm4 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0ZM10 18c1.74 0 3.04-.23 4-.61.02-.15.06-.3.12-.44a1.42 1.42 0 0 1-.96-.9v-.01l-.36-1.08-.03-.1a1.2 1.2 0 0 0-.26-.37l-.06-.06a1.2 1.2 0 0 0-.4-.24l-1.09-.35h-.01a1.42 1.42 0 0 1-.54-2.34H5.3c-1 0-1.81.81-1.81 1.81v.7c.04.77.25 1.75 1.13 2.55.95.87 2.6 1.44 5.37 1.44Zm5.23-7.65-.35-1.07a.42.42 0 0 0-.8 0l-.35 1.07a2.2 2.2 0 0 1-1.38 1.4l-1.07.35a.42.42 0 0 0 0 .8l1.07.34a2.2 2.2 0 0 1 1.4 1.4l.35 1.08a.42.42 0 0 0 .8 0l.35-1.07a2.2 2.2 0 0 1 1.4-1.4l1.07-.35a.42.42 0 0 0 0-.8h-.02l-1.08-.35a2.2 2.2 0 0 1-1.4-1.4Zm3.79 6.61.76.25h.02a.3.3 0 0 1 .14.47.3.3 0 0 1-.14.1l-.77.26a1.58 1.58 0 0 0-1 1l-.24.76a.3.3 0 0 1-.58 0l-.24-.77a1.57 1.57 0 0 0-1-1l-.77-.25a.3.3 0 0 1-.14-.46.3.3 0 0 1 .14-.1l.77-.26a1.58 1.58 0 0 0 .98-1l.25-.76a.3.3 0 0 1 .57 0l.25.77a1.58 1.58 0 0 0 1 1Z"/></g></svg>';
const FOUNDRY_AGENT_ICON_URI = `data:image/svg+xml;utf8,${encodeURIComponent(FOUNDRY_AGENT_SVG)}`;

const foundryAgentManifest: OperationManifest = {
  properties: {
    iconUri: FOUNDRY_AGENT_ICON_URI,
    brandColor: FOUNDRY_AGENT_BRAND,
    description:
      'Run an AI agent backed by Azure AI Foundry. Select or create a Foundry agent, then configure its model, instructions, and tools.',
    summary: 'Agent (Foundry)',

    allowChildOperations: true,
    subGraphDetails: {
      tools: {
        isAdditive: true,
        location: ['actions'],
        inputs: {
          type: 'object',
          properties: {
            description: {
              type: 'string',
              title: 'Description',
              description: 'Enter some explanation of when you would use this tool.',
            },
            agentParameterSchema: {
              title: 'Agent Parameters',
              description: 'Initialize Agent Parameters',
              type: 'object',
              'x-ms-visibility': 'important',
              'x-ms-editor': 'initializevariable',
              'x-ms-editor-options': {
                isAgentParameter: true,
              },
            },
          },
          required: ['description'],
        },
        inputsLocation: [],
        allowOperations: true,
      },
    },

    inputs: {
      type: 'object',
      properties: {
        agentModelType: {
          title: 'Agent model source',
          description: 'Source where your agent model is hosted.',
          type: 'string',
          default: 'FoundryAgentServiceV2',
          'x-ms-visibility': 'hideInUI',
          'x-ms-editor': 'dropdown',
          'x-ms-editor-options': {
            readOnly: true,
            options: [
              {
                value: 'FoundryAgentService',
                displayName: 'Foundry Agent Service',
              },
              {
                value: 'FoundryAgentServiceV2',
                displayName: 'Foundry Agent Service V2',
              },
            ],
          },
        },
        foundryAgentId: {
          type: 'string',
          title: 'Select agent',
          description: 'Select an existing Foundry agent or create a new one.',
          'x-ms-visibility': 'important',
          'x-ms-editor': 'combobox',
          'x-ms-editor-options': {
            autoComplete: 'off',
            allowCustomValue: false,
            externalLink: {
              builderId: 'foundryPortalAgent',
              label: 'Edit in Foundry portal',
            },
          },
          'x-ms-dynamic-list': {
            operationId: 'listFoundryAgents',
            itemsPath: 'data',
            itemValuePath: 'id',
            itemTitlePath: 'name',
          },
          // When an agent is picked, fan its definition into sibling parameters
          // so the user sees model, instructions, and version pre-populated
          // without a second round-trip. The resolver enriches each option's
          // `data` payload — see services/builtinDynamicListResolvers.ts.
          'x-ms-on-select': {
            foundryAgentName: 'name',
            foundryAgentVersion: 'agentVersion',
            _systemMessage: 'instructions',
            agentModelType: 'agentModelType',
            foundryVersionName: 'foundryVersionName',
            deploymentId: 'model',
          },
        },
        foundryAgentName: {
          type: 'string',
          title: 'Agent name',
          description: 'The display name of the selected Foundry agent.',
          'x-ms-visibility': 'internal',
        },
        foundryAgentVersion: {
          type: 'string',
          title: 'Agent version',
          description: 'Foundry agent API version — v1 (assistants) or v2 (agents).',
          'x-ms-visibility': 'internal',
        },
        foundryVersionName: {
          type: 'string',
          title: 'Version',
          description: 'Version of the selected Foundry agent definition.',
          'x-ms-visibility': 'important',
          'x-ms-editor': 'combobox',
          'x-ms-editor-options': {
            autoComplete: 'off',
            allowCustomValue: false,
          },
          'x-ms-dynamic-list': {
            operationId: 'listFoundryAgentVersions',
            parameters: {
              agentName: {
                parameterReference: 'foundryAgentId',
                required: true,
              },
            },
            itemsPath: 'data',
            itemValuePath: 'version',
            itemTitlePath: 'version',
          },
          'x-ms-input-dependencies': {
            parameters: [
              { parameterReference: 'foundryAgentId' },
              { parameterReference: 'agentModelType', requiredValues: ['FoundryAgentServiceV2'] },
            ],
          },
          'x-ms-on-select': {
            foundryVersionName: 'version',
            deploymentId: 'model',
            _systemMessage: 'instructions',
          },
        },
        deploymentId: {
          type: 'string',
          title: 'Model',
          description: 'Model used by the selected Foundry agent.',
          'x-ms-visibility': 'important',
          'x-ms-editor': 'combobox',
          'x-ms-connection-required': true,
          'x-ms-editor-options': {
            autoComplete: 'off',
            allowCustomValue: false,
          },
        },
        _systemMessage: {
          type: 'string',
          title: 'Instructions',
          description: 'Instructions that define the agent\'s behavior.',
          'x-ms-visibility': 'important',
          'x-ms-editor': 'textarea',
        },
        _userMessage: {
          type: 'string',
          title: 'Input message',
          description: 'The user prompt or question for the agent to respond to.',
          'x-ms-visibility': 'important',
          'x-ms-editor': 'textarea',
        },
        agentModelSettings: {
          type: 'object',
          'x-ms-visibility': 'advanced',
          properties: {
            agentChatCompletionSettings: {
              type: 'object',
              properties: {
                temperature: {
                  title: 'Temperature',
                  description: 'Sampling temperature to use (value should be between 0 and 2)',
                  type: 'number',
                  format: 'float',
                  minimum: 0,
                  maximum: 2.0,
                },
                responseFormat: {
                  type: 'object',
                  properties: {
                    type: {
                      type: 'string',
                      title: 'Response format type',
                      'x-ms-editor': 'dropdown',
                      'x-ms-editor-options': {
                        options: [
                          {
                            value: 'json_schema',
                            displayName: 'JSON Schema',
                          },
                          {
                            value: 'json_object',
                            displayName: 'JSON Object',
                          },
                          {
                            value: 'text',
                            displayName: 'Text',
                          },
                        ],
                      },
                    },
                    json_schema: {
                      title: 'JSON Schema response format',
                      description: 'Schema used to format the agent response.',
                      type: 'object',
                      'x-ms-editor': 'schema',
                      'x-ms-input-dependencies': {
                        type: 'visibility',
                        parameters: [
                          {
                            name: 'agentModelSettings.agentChatCompletionSettings.responseFormat.type',
                            values: ['json_schema'],
                          },
                        ],
                      },
                    },
                  },
                },
                topP: {
                  title: 'Top P',
                  description: 'Nucleus sampling parameter (value should be between 0 and 1)',
                  type: 'number',
                  format: 'float',
                  minimum: 0,
                  maximum: 1.0,
                },
                reasoningEffort: {
                  type: 'string',
                  title: 'Reasoning effort',
                  'x-ms-editor': 'dropdown',
                  'x-ms-editor-options': {
                    options: [
                      {
                        value: 'low',
                        displayName: 'Low',
                      },
                      {
                        value: 'medium',
                        displayName: 'Medium',
                      },
                      {
                        value: 'high',
                        displayName: 'High',
                      },
                      {
                        value: 'none',
                        displayName: 'None',
                      },
                      {
                        value: 'minimal',
                        displayName: 'Minimal',
                      },
                    ],
                  },
                },
                builtinTools: {
                  type: 'array',
                  title: 'Built in tools',
                  'x-ms-editor': 'dropdown',
                  'x-ms-editor-options': {
                    multiSelect: true,
                    titleSeparator: ',',
                    serialization: {
                      valueType: 'array',
                    },
                    options: [
                      {
                        value: 'code_interpreter',
                        displayName: 'Code interpreter',
                      },
                      {
                        value: 'file_search',
                        displayName: 'File search',
                      },
                    ],
                  },
                },
              },
            },
          },
        },
      },
      required: ['agentModelType', 'foundryAgentId', 'deploymentId', '_userMessage'],
    },

    outputs: {
      type: 'object',
      properties: {
        lastAssistantMessage: {
          type: 'object',
          title: 'Last Assistant Message',
          description: 'This is the final message returned by the agent',
        },
      },
    },
    isOutputsOptional: false,

    inputsLocation: ['inputs', 'parameters'],
    isInputsOptional: false,

    connection: {
      required: true,
      type: 'agent',
    },

    connectionReference: {
      referenceKeyFormat: 'agentconnection',
    },

    connector: {
      id: '/connectionProviders/agent',
      name: 'Agent',
      properties: {
        description: 'Azure AI Foundry Agent operations',
        displayName: 'Agent (Foundry)',
      },
      isSecureByDefault: true,
    } as ConnectorInfo,

    settings: {
      trackedProperties: {
        scopes: [SettingScope.Action],
      },
      timeout: {
        scopes: [SettingScope.Action],
      },
      count: {
        scopes: [SettingScope.Action],
      },
      retryPolicy: {
        scopes: [SettingScope.Action],
      },
      operationOptions: {
        scopes: [SettingScope.Action],
        options: [OperationOptions.FailWhenLimitsReached],
      },
    },
  },
};

export default foundryAgentManifest;
